import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button, Text } from "@core/index";
import { MicVocal, CheckCircle, Volume2, Clock, Users } from "lucide-react";
import { useListSpokenDialogues } from "@packages/api/madlenAPI";
import type { ListSpokenDialoguesParams } from "@packages/api/madlenAPI.schemas";
import podcastBanner from "@packages/assets/banners/podcast.png";

const SpokenDialogueView = () => {
  const { t, i18n } = useTranslation();
  const [page] = useState<number>(1);
  const [size] = useState<number>(30);

  const params = useMemo<ListSpokenDialoguesParams>(
    () => ({ page, size }),
    [page, size],
  );
  const { data, isLoading, isError } = useListSpokenDialogues(params, {
    query: {},
  });
  const spokenDialogues = data?.data ?? [];
  const bannerImageUrl = podcastBanner;

  const formatRelativeTime = (input?: string | number | Date) => {
    if (!input) return "";
    const date = new Date(input);
    if (isNaN(date.getTime())) return "";
    const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
    const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" });
    const units: [Intl.RelativeTimeFormatUnit, number][] = [
      ["year", 60 * 60 * 24 * 365],
      ["month", 60 * 60 * 24 * 30],
      ["week", 60 * 60 * 24 * 7],
      ["day", 60 * 60 * 24],
      ["hour", 60 * 60],
      ["minute", 60],
      ["second", 1],
    ];
    for (const [unit, sec] of units) {
      if (Math.abs(diffSeconds) >= sec || unit === "second") {
        const value = Math.round(diffSeconds / sec);
        return rtf.format(value, unit);
      }
    }
    return "";
  };

  return (
    <div className="flex-1 md:p-8 p-4 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        <div className="mb-4 sm:mb-6 lg:mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
          <div className="min-w-0 flex-1">
            <Text
              variant="h1"
              className="text-lg sm:text-xl lg:text-2xl xl:text-3xl break-words"
            >
              {t("spokenDialogue.title")}
            </Text>
            <Text
              variant="p"
              className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base break-words"
            >
              {t("spokenDialogue.description")}
            </Text>
          </div>
          <div className="flex-shrink-0 w-full sm:w-auto">
            <Link to="/content/spoken-dialogue/create">
              <Button
                size="md"
                rounded="full"
                className="bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white w-full sm:w-auto"
              >
                <span className="flex items-center gap-2 justify-center">
                  <MicVocal className="w-4 h-4" />
                  {t("spokenDialogue.createButton")}
                </span>
              </Button>
            </Link>
          </div>
        </div>

        {/* Banner under header: left image, right text with emphasized CTA word */}
        <div className="mb-4 sm:mb-6 lg:mb-8">
          <div className="relative overflow-hidden rounded-2xl border border-dark-200 dark:border-dark-400 bg-white dark:bg-dark-600">
            {/* Gradient background overlay */}
            <div
              className="absolute inset-0 pointer-events-none z-0"
              style={{
                background:
                  "linear-gradient(135deg, rgba(79, 70, 229, 0.10) 0%, rgba(0, 0, 0, 0.85) 100%)",
              }}
            />
            <div className="flex flex-col md:flex-row h-full p-4 items-center relative z-10">
              {/* Left image */}
              <div className="md:w-2/6 w-full h-28 relative flex items-center justify-start">
                <img
                  src={bannerImageUrl}
                  alt=""
                  className="w-full h-full object-contain"
                  style={{ position: "static" }}
                />
              </div>
              {/* Right text */}
              <div className="flex-1 p-5 sm:p-6 lg:p-8 flex items-end justify-end">
                <div>
                  <Text
                    variant="p"
                    className="text-gray-700 dark:text-gray-200 text-sm sm:text-base lg:text-lg leading-relaxed"
                  >
                    Create engaging <b>spoken dialogues</b>
                    <br />
                    for your students to practice conversations.
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content card */}
        <div className="mt-3 sm:mt-4 lg:mt-6">
          {isLoading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500 mx-auto mb-4"></div>
              <Text variant="p" className="text-gray-600 dark:text-gray-400">
                {t("common.loading")}...
              </Text>
            </div>
          ) : isError ? (
            <div className="text-center py-16">
              <Text variant="p" className="text-red-500">
                {t("common.error")}
              </Text>
            </div>
          ) : spokenDialogues.length === 0 ? (
            <div className="text-center py-16">
              <div className="mx-auto w-24 h-24 bg-indigo-100 dark:bg-indigo-900 rounded-full flex items-center justify-center mb-6">
                <MicVocal className="w-12 h-12 text-indigo-600 dark:text-indigo-400" />
              </div>
              <Text variant="h2" className="mb-2">
                {t("spokenDialogue.empty.title")}
              </Text>
              <Text
                variant="p"
                status="secondary"
                className="mb-8 max-w-md mx-auto"
              >
                {t("spokenDialogue.empty.description")}
              </Text>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {spokenDialogues.map((spokenDialogue) => (
                <div
                  key={spokenDialogue.id}
                  className="bg-white dark:bg-dark-600 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900 rounded-full flex items-center justify-center">
                        <Volume2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white truncate max-w-64">
                          {spokenDialogue.title &&
                          String(spokenDialogue.title).trim().length > 0
                            ? spokenDialogue.title
                            : spokenDialogue.scenario ||
                              t(
                                "spokenDialogue.create.basicInfo.titlePlaceholder",
                              )}
                        </h3>
                      </div>
                    </div>
                    {/* Status Badge */}
                    <div className="flex items-center">
                      {spokenDialogue.status === "READY" && (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                      {spokenDialogue.status === "PROCESSING" && (
                        <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                      )}
                      {!spokenDialogue.status && (
                        <div className="w-5 h-5 bg-gray-300 rounded-full"></div>
                      )}
                    </div>
                  </div>

                  {/* Spoken Dialogue details */}
                  <div className="mb-4 space-y-2">
                    {/* Content Language and Accent */}
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-gray-500 dark:text-gray-400">
                        {t("interactive_material.create.contentLanguage")}:
                      </span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        {spokenDialogue.language === "en" &&
                          t("interactive_material.create.contentLanguages.en")}
                        {spokenDialogue.language === "tr" &&
                          t("interactive_material.create.contentLanguages.tr")}
                        {spokenDialogue.language === "de" &&
                          t("interactive_material.create.contentLanguages.de")}
                        {spokenDialogue.language === "fr" &&
                          t("interactive_material.create.contentLanguages.fr")}
                        {spokenDialogue.language === "it" &&
                          t("interactive_material.create.contentLanguages.it")}
                        {spokenDialogue.language === "es" &&
                          t("interactive_material.create.contentLanguages.es")}
                      </span>
                      {spokenDialogue.accent &&
                        spokenDialogue.language === "en" && (
                          <>
                            <span className="text-gray-400">•</span>
                            <span className="font-medium text-gray-700 dark:text-gray-300">
                              {spokenDialogue.accent === "BRITISH" && "British"}
                              {spokenDialogue.accent === "AMERICAN" &&
                                "American"}
                            </span>
                          </>
                        )}
                    </div>

                    {/* CEFR Level and Duration */}
                    <div className="flex items-center gap-4 text-xs">
                      {spokenDialogue.cefrLevel && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 dark:text-gray-400">
                            Level:
                          </span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            {spokenDialogue.cefrLevel === "PRE_A1" && "Pre-A1"}
                            {spokenDialogue.cefrLevel === "A1" && "A1"}
                            {spokenDialogue.cefrLevel === "A2" && "A2"}
                            {spokenDialogue.cefrLevel === "B1" && "B1"}
                            {spokenDialogue.cefrLevel === "B2" && "B2"}
                            {spokenDialogue.cefrLevel === "C1" && "C1"}
                            {spokenDialogue.cefrLevel === "C2" && "C2"}
                          </span>
                        </div>
                      )}

                      {spokenDialogue.duration && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                          <span className="text-gray-500 dark:text-gray-400">
                            {spokenDialogue.duration} min
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="text-xs text-gray-500 dark:text-gray-400"></div>
                    <Link to={`/content/spoken-dialogue/${spokenDialogue.id}`}>
                      <Button
                        variant="outline"
                        size="sm"
                        rounded="full"
                        className="border-indigo-200 text-indigo-600 hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-400 dark:hover:bg-indigo-900/20"
                        disabled={spokenDialogue.status === "PROCESSING"}
                      >
                        {t("common.view")}
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpokenDialogueView;
